<template>
	<div>
		<!-- Employment Section -->
		<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
			<FormKit type="text" name="employerName" label="Naam werkgever" />

			<FormKit type="loanlinkDate" name="startDate" label="In dienst sinds" />

			<FormKit
				type="select"
				select-icon="down"
				name="professionType"
				label="Beroepstype"
				:options="professionTypeOptions"
			/>

			<FormKit type="text" name="professionFunction" label="Beroepsfunctie" />

			<FormKit type="text" name="location" label="Vestigingsplaats" />

			<FormKit
				type="select"
				select-icon="down"
				name="countryEmployer"
				label="Land werkgever"
				placeholder="selecteren..."
				:options="sortedCountries"
			/>

			<FormKit
				type="loanlinkNumeric"
				name="hoursPerWeek"
				label="Gemiddeld aantal uren per week"
			/>

			<FormKit
				type="loanlinkOption"
				name="permanentPosition"
				label="Vaste aanstelling"
				:options="permanentPositionOptions"
			/>

			<FormKit
				type="loanlinkOption"
				name="isEmployedByFamily"
				label="In dienst bij familie"
				:options="yesNoOptions"
			/>
		</div>

		<!-- Benefits Section -->
		<div class="mt-4">
			<h4 class="mb-2 font-medium">Arbeidsvoorwaarden</h4>
			<div class="space-y-2">
				<FormKit
					type="checkbox"
					decorator-icon="check"
					name="benefits"
					:options="benefitOptions"
				/>
			</div>
		</div>

		<!-- Period & Salary Section -->
		<div class="mt-6">
			<h4 class="mb-2 font-medium">Salaris</h4>
			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<FormKit
					v-model="salaryPeriod"
					type="loanlinkOption"
					name="salaryPeriod"
					label="Periode"
					:options="salaryPeriodOptions"
				/>

				<div>
					<FormKit
						v-model="grossSalaryModel"
						type="loanlinkCurrency"
						name="grossSalary"
						label="Bruto salaris"
						:help="
							salaryPeriod
								? `Bedrag per ${salaryPeriodOptions[salaryPeriod].toLowerCase()}`
								: 'Bedrag per periode'
						"
					/>
					<FormKit
						v-model="salaryIncludesVacation"
						type="loanlinkOption"
						name="salaryIncludesVacation"
						label="Inclusief vakantietoeslag (8%)"
						:options="yesNoOptions"
					/>
				</div>
			</div>

			<template v-if="salaryIncludesVacation && grossSalaryExcl != null">
				<div class="grid grid-cols-1 gap-4 md:grid-cols-2 mt-4">
					<div class="salary-breakdown__detail-card">
						<div class="salary-breakdown__row">
							<span>Basissalaris excl. vakantiegeld</span>
							<span>{{ formatCurrency(grossSalaryExcl) }}</span>
						</div>
						<div class="salary-breakdown__row">
							<span>Vakantiegeld (8%)</span>
							<span>{{
								vacationAllowanceCalc != null
									? formatCurrency(vacationAllowanceCalc)
									: "—"
							}}</span>
						</div>
					</div>
					<div class="salary-breakdown__total-card">
						<div class="salary-breakdown__total-label">
							Totaal bruto salaris
						</div>
						<div class="salary-breakdown__total-amount">
							{{ formatCurrency(grossSalaryModel ?? 0) }}
						</div>
						<p class="salary-breakdown__help">
							Dit is het bedrag waarmee we rekenen.
						</p>
					</div>
				</div>
			</template>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2 mt-4">
				<FormKit
					type="loanlinkCurrency"
					name="employeePensionContribution"
					label="Werknemersbijdrage pensioen"
				/>

				<FormKit
					type="loanlinkCurrency"
					name="employeeAOPremium"
					label="Werknemersbijdrage AO-verzekering"
					help="Per maand"
				/>
			</div>
		</div>

		<!-- Additional Income Components -->
		<div class="mt-6">
			<h4 class="mb-2 font-medium">
				Aanvullende inkomenscomponenten (per jaar)
			</h4>
			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<FormKit
					type="loanlinkCurrency"
					name="vacationAllowance"
					label="Vakantietoeslag"
				/>

				<FormKit
					type="loanlinkCurrency"
					name="thirteenthMonth"
					label="Vaste 13e maand"
				/>

				<FormKit type="loanlinkCurrency" name="overtime" label="Overwerk" />

				<FormKit
					type="loanlinkCurrency"
					name="irregularHoursAllowance"
					label="Onregelmatigheidstoeslag"
				/>

				<FormKit type="loanlinkCurrency" name="commission" label="Provisie" />

				<FormKit
					type="loanlinkCurrency"
					name="yearEndBonus"
					label="Vaste eindejaarsuitkering"
				/>

				<FormKit
					type="loanlinkPercentage"
					name="percentageForToetsinkomen"
					label="Percentage meetellen voor toetsinkomen"
				/>
			</div>
		</div>

		<!-- Company Vehicle Section -->
		<div class="mt-6">
			<FormKit
				v-model="hasCompanyVehicle"
				type="loanlinkOption"
				name="hasCompanyVehicle"
				label="Auto/Fiets van de zaak"
				:options="hasCompanyVehicleOptions"
			/>

			<div
				v-if="hasCompanyVehicle"
				class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2"
			>
				<FormKit
					type="select"
					select-icon="down"
					name="vehicleScheme"
					label="Regeling"
					:options="vehicleSchemeOptions"
				/>

				<FormKit
					type="loanlinkCurrency"
					name="vehicleOwnContribution"
					label="Eigen bijdrage"
					help="Per jaar"
				/>

				<FormKit
					type="loanlinkCurrency"
					name="vehicleCatalogValue"
					label="Cataloguswaarde"
				/>

				<FormKit
					type="loanlinkPercentage"
					name="vehicleAdditionPercentage"
					label="Fiscale bijtelling"
				/>
			</div>
		</div>

		<!-- Income Calculation Method -->
		<div class="mt-6">
			<h4 class="mb-2 font-medium">Berekeningsmethode toetsinkomen</h4>
			<FormKit
				v-model="calculationMethod"
				type="loanlinkOption"
				name="calculationMethod"
				:options="calculationMethodOptions"
			/>

			<div
				v-if="calculationMethod === 'uwvVerzekeringsbericht'"
				class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2"
			>
				<div class="flex items-end gap-2">
					<FormKit
						type="loanlinkCurrency"
						name="uwvToetsinkomen"
						label="Toetsinkomen o.b.v. UWV-verzekeringsbericht"
					/>
				</div>
			</div>

			<div class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2">
				<FormKit
					type="loanlinkCurrency"
					name="monthlyPensionContribution"
					label="Maandelijkse pensioenbijdrage"
				/>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import { sortedCountries } from "@/models/inventarisation/countryOptions";
import {
	professionTypeOptions,
	benefitOptions,
	vehicleSchemeOptions,
	calculationMethodOptions,
	permanentPositionOptions,
	salaryPeriodOptions,
	hasCompanyVehicleOptions,
	yesNoOptions,
} from "@/models/inventarisation/income/loondienstOptions";

const calculationMethod = ref();
const hasCompanyVehicle = ref();
const salaryPeriod = ref<keyof typeof salaryPeriodOptions | undefined>();
const grossSalaryModel = ref<number | null>(null);
const salaryIncludesVacation = ref(false);

const grossSalaryExcl = computed<number | null>(() => {
	if (grossSalaryModel.value == null) return null;
	return grossSalaryModel.value / 1.08;
});

const vacationAllowanceCalc = computed<number | null>(() => {
	if (grossSalaryModel.value == null || grossSalaryExcl.value == null)
		return null;
	return grossSalaryModel.value - grossSalaryExcl.value;
});

function formatCurrency(value: number): string {
	return new Intl.NumberFormat("nl-NL", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 0,
	}).format(value);
}
</script>

<style scoped>
.salary-breakdown__detail-card,
.salary-breakdown__total-card {
	border: 1px solid var(--border);
	background: var(--background-secondary);
	border-radius: var(--border-radius);
	padding: 0.75rem 1rem;
	font-size: 0.875rem;
	max-width: 280px;
}

.salary-breakdown__row {
	display: flex;
	justify-content: space-between;
	color: var(--text-secondary);
}

.salary-breakdown__row + .salary-breakdown__row {
	margin-top: 0.25rem;
}

.salary-breakdown__row span:last-child {
	font-weight: 500;
	color: var(--text-primary);
	margin-left: 1rem;
	white-space: nowrap;
}

.salary-breakdown__total-label {
	font-weight: 600;
	color: var(--text-primary);
}

.salary-breakdown__total-amount {
	font-size: 1.25rem;
	font-weight: 700;
	color: var(--text-primary);
	margin-top: 0.25rem;
}

.salary-breakdown__help {
	margin-top: 0.25rem;
	font-size: 0.75rem;
	color: var(--text-muted);
}
</style>
